import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Loader2, Check, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "../../lib/api";

const RunLog = ({ run }) => (
  <div data-testid="tester-run" className="border border-[#0E1B3D]/[0.08] bg-[#FFFFFF]">
    <div className="flex flex-wrap items-center gap-4 px-5 py-4 border-b border-[#0E1B3D]/[0.08]">
      <p className="text-sm text-[#0E1B3D]">{format(new Date(run.created_at), "MMM d, yyyy · h:mm a")}</p>
      <span data-testid="tester-run-action" className="font-mono-x text-[0.65rem] tracking-[0.18em] uppercase text-[#D7263D]">{LABELS[run.action] || "Full round"}</span>
      <span data-testid="tester-run-status" className="font-mono-x text-[0.65rem] tracking-[0.18em] uppercase text-[#4A5878]">{run.status === "running" ? "In progress" : run.status === "error" ? `Error: ${run.error}` : "Done"}</span>
      <span data-testid="tester-run-passed" className="font-mono-x text-[0.65rem] tracking-[0.18em] uppercase text-[#2F6FD6]">{run.log.filter((e) => e.ok).length} passed</span>
      <span data-testid="tester-run-failed" className={`font-mono-x text-[0.65rem] tracking-[0.18em] uppercase ${run.log.some((e) => !e.ok) ? "text-[#D7263D]" : "text-[#4A5878]"}`}>{run.log.filter((e) => !e.ok).length} failed</span>
    </div>
    <ul className="max-h-[420px] overflow-y-auto divide-y divide-[#0E1B3D]/[0.05]">
      {run.log.map((e, i) => (
        <li key={i} data-testid="tester-log-row" className="flex items-start gap-3 px-5 py-2.5 text-sm">
          {e.ok ? <Check className="w-4 h-4 mt-0.5 text-[#2F6FD6] shrink-0" /> : <X className="w-4 h-4 mt-0.5 text-[#D7263D] shrink-0" />}
          <span className="w-36 shrink-0 text-[#0E1B3D]">{e.tester}</span>
          <span className="text-[#26355A]">{e.action}</span>
          {e.detail && <span className="ml-auto text-xs text-[#4A5878] text-right truncate max-w-[45%]">{e.detail}</span>}
        </li>
      ))}
    </ul>
  </div>
);

const ACTIONS = [
  ["likes", "Likes", "1,200 crowd testers add 100–500 likes to every post (random per post), plus 8 core testers like through the app"],
  ["comments", "Comments", "4–10 new comments per post written around each post's caption and vibe"],
  ["logins", "Logins", "Core testers and 25 random crowd members sign in and check their session"],
  ["subscriptions", "Subscriptions", "Core testers subscribe or upgrade with Stripe test cards"],
  ["tips", "Tips", "Subscribed testers send 1–2 real test tips each on random posts"],
  ["ppv", "PPV unlocks", "Each subscribed tester buys one pay-per-view post"],
  ["messages", "Messages", "Subscribed testers send you a DM"],
  ["access", "Access checks", "Testers open the feed and confirm locked posts stay locked"],
  ["all", "Full round", "Everything above in one go, like a normal day of fans"],
];
const LABELS = Object.fromEntries(ACTIONS.map(([k, l]) => [k, l]));

export function Testers({ onRan }) {
  const [runs, setRuns] = useState([]);
  const [starting, setStarting] = useState(null);
  const busy = Boolean(starting) || runs[0]?.status === "running";
  const load = () => api.get("/admin/testers/runs").then((r) => setRuns(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (runs[0]?.status !== "running") return undefined;
    const t = setTimeout(async () => {
      const { data } = await api.get("/admin/testers/runs");
      setRuns(data);
      if (data[0]?.status === "done") {
        toast.success(`${LABELS[data[0].action] || "Tester round"} done: ${data[0].passed} passed, ${data[0].failed} failed`);
        onRan?.();
      }
    }, 3000);
    return () => clearTimeout(t);
  }, [runs, onRan]);
  const run = async (action) => {
    setStarting(action);
    try {
      await api.post("/admin/testers/run", { action });
      await load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setStarting(null);
    }
  };
  const clear = async () => {
    if (!window.confirm("Remove every tester account and all their likes, comments, tips, PPV unlocks and messages?")) return;
    try {
      const { data } = await api.post("/admin/testers/clear");
      toast.success(`Removed ${data.removed_accounts} tester accounts and their activity`);
      setRuns([]);
      onRan?.();
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  const active = starting || (runs[0]?.status === "running" && runs[0]?.action);
  return (
    <div data-testid="admin-testers" className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-[#26355A] max-w-xl">Test one thing at a time. Each button sends testers to do just that, then reports what worked.</p>
        <button data-testid="admin-clear-testers-btn" disabled={busy} onClick={clear} className="ghost-btn px-5 py-3 text-xs tracking-[0.16em] uppercase flex items-center gap-2 disabled:opacity-60">
          <Trash2 className="w-4 h-4" /> Clear tester activity
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {ACTIONS.map(([k, l, d]) => (
          <button key={k} data-testid={`tester-action-${k}`} disabled={busy} onClick={() => run(k)}
            className={`text-left border p-4 bg-[#FFFFFF] transition-colors hover:border-[#D7263D] disabled:opacity-60 disabled:hover:border-[#0E1B3D]/[0.08] ${k === "all" ? "border-[#D7263D]/50" : "border-[#0E1B3D]/[0.08]"}`}>
            <span className="flex items-center gap-2 font-display text-lg text-[#0E1B3D]">
              {active === k && <Loader2 className="w-4 h-4 animate-spin text-[#D7263D]" />}{l}
            </span>
            <span className="block mt-1 text-xs text-[#4A5878] leading-relaxed">{d}</span>
          </button>
        ))}
      </div>
      {runs.length === 0 && <p data-testid="tester-no-runs" className="text-sm text-[#4A5878]">No tester runs yet.</p>}
      {runs.slice(0, 3).map((r) => <RunLog key={r.id} run={r} />)}
    </div>
  );
}
